import type { SidebarLayoutMode } from "@cmmd-forge/contracts/settings";
import type { Theme } from "../../hooks/useTheme";
import { useState, type ReactElement, type ReactNode } from "react";

import type { GitHubAgentCapability } from "../../githubAgentCapability";
import {
  PLUGIN_FALLBACKS,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "../plugins/pluginsPage.logic";
import type { PluginConnectionAttempts } from "../plugins/pluginConnections";
import { FirstAccountSetupAppAccessPanel } from "./FirstAccountSetupAppAccessPanel";
import { FirstAccountSetupAppearancePanel } from "./FirstAccountSetupAppearancePanel";
import { ProviderRow } from "./FirstAccountSetupParts";
import {
  FirstAccountSetupProjectPanel,
  FirstAccountSetupReadyPanel,
} from "./FirstAccountSetupProjectPanels";
import type {
  ProviderSetupRow,
  SetupProjectRow,
  SetupProjectWorkspaceOption,
  SetupStepId,
  UsageSource,
} from "./firstAccountSetupData";
import { FirstAccountSetupEnvironmentPanel } from "./FirstAccountSetupEnvironmentPanel";
import type { FirstRunEnvironmentPhase } from "./useFirstRunEnvironment";

function ProviderCodePromptRow(props: {
  readonly busy: boolean;
  readonly authorizeUrl: string | null;
  readonly onSubmit: (code: string) => void;
}): ReactElement {
  const [code, setCode] = useState("");
  const trimmed = code.trim();
  return (
    <div className="border-t border-border px-4 py-3">
      <h4 className="text-xs font-semibold text-foreground">Finish connecting Claude</h4>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        Open the authorization link, sign in to Claude, then paste the code it shows you.
      </p>
      {props.authorizeUrl ? (
        <a
          href={props.authorizeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          Open Claude authorization page
        </a>
      ) : null}
      <form
        className="mt-2 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed) {
            props.onSubmit(trimmed);
          }
        }}
      >
        <input
          type="text"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Paste your code"
          className="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 text-xs text-foreground placeholder:text-muted-foreground"
        />
        <button
          type="submit"
          disabled={props.busy || trimmed.length === 0}
          className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          Connect
        </button>
      </form>
    </div>
  );
}

function ProviderRuntimeFallbackRow(props: {
  readonly providerId: ProviderSetupRow["id"];
  readonly busy: boolean;
  readonly onRetry: (providerId: ProviderSetupRow["id"]) => void;
}): ReactElement {
  return (
    <div className="border-t border-border px-4 py-3">
      <h4 className="text-xs font-semibold text-foreground">
        Personal sign-in needs the provider CLI
      </h4>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        Install the provider CLI here, or open Forge where it is installed, then retry, or keep
        using workspace credits.
      </p>
      <button
        type="button"
        disabled={props.busy}
        onClick={() => props.onRetry(props.providerId)}
        className="mt-2 inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        Retry
      </button>
    </div>
  );
}

function FirstAccountSetupAgentPanel(props: {
  readonly providerRows: readonly ProviderSetupRow[];
  readonly providerActionLabel: string | null;
  readonly providerActionBusy: boolean;
  readonly providerActionProviderId: ProviderSetupRow["id"] | null;
  readonly providerCodePrompt: boolean;
  readonly providerAuthorizeUrl: string | null;
  readonly providerNeedsRuntime: boolean;
  readonly codexCloudSignInSlot: ReactNode;
  readonly claudeCloudSignInSlot: ReactNode;
  readonly onProviderAction: (providerId: ProviderSetupRow["id"]) => void;
  readonly onSubmitProviderCode: (code: string) => void;
  readonly onUsageSourceChange: (source: UsageSource) => void;
}): ReactElement {
  return (
    <div className="mt-6">
      <section className="overflow-hidden rounded-lg border border-border bg-background/70">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Agent access</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose which account Forge should use when each agent runs.
          </p>
        </div>
        {props.providerRows.length === 0 ? (
          <div className="px-4 py-4">
            <h4 className="text-sm font-semibold text-foreground">Provider status pending</h4>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">
              Forge has not received live provider data for this session.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-0 border-collapse text-left sm:min-w-[34rem]">
              <thead>
                <tr className="text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                  <th className="px-3 py-3">Provider</th>
                  <th className="px-3 py-3 text-right">Account</th>
                </tr>
              </thead>
              <tbody>
                {props.providerRows.map((row) => (
                  <ProviderRow
                    key={row.id}
                    row={row}
                    actionLabel={
                      props.providerActionProviderId === row.id ? props.providerActionLabel : null
                    }
                    actionBusy={
                      props.providerActionProviderId === row.id && props.providerActionBusy
                    }
                    onUseWorkspaceCredits={() => props.onUsageSourceChange("workspace")}
                    onAction={props.onProviderAction}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {props.providerCodePrompt ? (
          <ProviderCodePromptRow
            busy={props.providerActionBusy}
            authorizeUrl={props.providerAuthorizeUrl}
            onSubmit={props.onSubmitProviderCode}
          />
        ) : null}
        {props.providerNeedsRuntime && props.providerActionProviderId ? (
          <ProviderRuntimeFallbackRow
            providerId={props.providerActionProviderId}
            busy={props.providerActionBusy}
            onRetry={props.onProviderAction}
          />
        ) : null}
        {props.codexCloudSignInSlot ? (
          <div className="border-t border-border px-4 py-3">{props.codexCloudSignInSlot}</div>
        ) : null}
        {props.claudeCloudSignInSlot ? (
          <div className="border-t border-border px-4 py-3">{props.claudeCloudSignInSlot}</div>
        ) : null}
      </section>
    </div>
  );
}

export function FirstAccountSetupStepPanel(props: {
  readonly activeStepId: SetupStepId;
  readonly providerRows: readonly ProviderSetupRow[];
  readonly projects: readonly SetupProjectRow[];
  readonly projectWorkspaceOptions: readonly SetupProjectWorkspaceOption[];
  readonly projectsLoading: boolean;
  readonly selectedProjectId: string | null;
  readonly selectedProviderLabel: string;
  readonly theme: Theme;
  readonly uiFontSize: number;
  readonly codeFontSize: number;
  readonly githubCapability: GitHubAgentCapability | null;
  readonly githubCapabilityLoading: boolean;
  readonly githubActionBusy: boolean;
  readonly githubAuthorizeUrl: string | null;
  readonly githubAuthorizeUrlLoading: boolean;
  readonly plugins?: ReadonlyArray<ForgePlugin>;
  readonly pluginsLoading: boolean;
  readonly pluginsError: string | null;
  readonly pluginConnectionAttempt: PluginConnectionAttempt | null;
  readonly pluginConnectionAttempts: PluginConnectionAttempts;
  readonly providerActionLabel: string | null;
  readonly providerActionBusy: boolean;
  readonly providerActionProviderId: ProviderSetupRow["id"] | null;
  readonly providerCodePrompt: boolean;
  readonly providerAuthorizeUrl: string | null;
  readonly providerNeedsRuntime: boolean;
  readonly codexCloudSignInSlot: ReactNode;
  readonly claudeCloudSignInSlot: ReactNode;
  readonly onSubmitProviderCode: (code: string) => void;
  readonly onGitHubConnect: () => void;
  readonly onPluginConnect: (pluginId: ForgePluginId) => void;
  readonly onOpenProjectImporter: ((project?: SetupProjectRow) => void) | undefined;
  readonly onProjectChange: (projectId: string) => void;
  readonly onProviderAction: (providerId: ProviderSetupRow["id"]) => void;
  readonly sidebarLayoutMode: SidebarLayoutMode;
  readonly onThemeChange: (theme: Theme) => void;
  readonly onUiFontSizeChange: (fontSize: number) => void;
  readonly onCodeFontSizeChange: (fontSize: number) => void;
  readonly onSidebarLayoutModeChange: (mode: SidebarLayoutMode) => void;
  readonly onUsageSourceChange: (source: UsageSource) => void;
  readonly environmentPhase?: FirstRunEnvironmentPhase;
  readonly environmentId?: string | null;
  readonly environmentError?: string | null;
  readonly environmentWorkspaceLabel?: string | null;
  readonly environmentWorkspaceOptions?: ReadonlyArray<{
    readonly id: number;
    readonly name: string;
  }>;
  readonly onSelectEnvironmentWorkspace?: (organizationId: number) => void;
  readonly onEnsureEnvironment?: () => void;
}): ReactElement {
  if (props.activeStepId === "environment") {
    return <FirstAccountSetupEnvironmentPanel {...props} />;
  }

  if (props.activeStepId === "account") {
    return <FirstAccountSetupAppearancePanel {...props} />;
  }

  if (props.activeStepId === "usage" || props.activeStepId === "providers") {
    return <FirstAccountSetupAgentPanel {...props} />;
  }

  if (props.activeStepId === "projects") {
    return <FirstAccountSetupProjectPanel {...props} />;
  }

  if (props.activeStepId === "capabilities") {
    return (
      <FirstAccountSetupAppAccessPanel
        githubCapability={props.githubCapability}
        githubCapabilityLoading={props.githubCapabilityLoading}
        githubActionBusy={props.githubActionBusy}
        githubAuthorizeUrl={props.githubAuthorizeUrl}
        githubAuthorizeUrlLoading={props.githubAuthorizeUrlLoading}
        plugins={props.plugins ?? PLUGIN_FALLBACKS}
        pluginsLoading={props.pluginsLoading}
        pluginsError={props.pluginsError}
        pluginConnectionAttempt={props.pluginConnectionAttempt}
        pluginConnectionAttempts={props.pluginConnectionAttempts}
        onGitHubConnect={props.onGitHubConnect}
        onPluginConnect={props.onPluginConnect}
      />
    );
  }

  if (props.activeStepId === "first-run") {
    return <FirstAccountSetupReadyPanel {...props} />;
  }

  return (
    <div className="mt-6 rounded-lg border border-border bg-background/70 p-4">
      <h3 className="text-sm font-semibold text-foreground">Ready to continue</h3>
      <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">
        Forge will use the current project context for this step. Nothing here locks the project to
        one runtime.
      </p>
    </div>
  );
}
